import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Stack,
  Avatar,
  Paper,
  Chip,
  alpha,
  Divider
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  ArrowBack as ArrowBackIcon,
  Timeline as TimelineIcon,
  Groups as GroupsIcon,
  TrendingUp as TrendingIcon,
  Public as PublicIcon,
  Verified as VerifiedIcon,
  Speed as SpeedIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  GitHub as GitHubIcon
} from '@mui/icons-material';

import civiqLogo from '../img/civiq.jpg';

const About = ({ onNavigate }) => {
  const team = [
    {
      name: 'Rajesh Kumar',
      role: 'CEO & Co-founder',
      image: '/api/placeholder/150/150',
      description: 'Former election analyst with 15+ years experience in political data science',
      linkedin: '#',
      twitter: '#'
    },
    {
      name: 'Priya Sharma',
      role: 'CTO & Co-founder',
      image: '/api/placeholder/150/150',
      description: 'Ex-Google engineer specializing in large-scale data systems and AI',
      linkedin: '#',
      github: '#'
    },
    {
      name: 'Amit Singh',
      role: 'Head of Data Science',
      image: '/api/placeholder/150/150',
      description: 'PhD in Political Science from JNU, expert in Indian electoral patterns',
      linkedin: '#',
      twitter: '#'
    },
    {
      name: 'Meera Reddy',
      role: 'Product Manager',
      image: '/api/placeholder/150/150',
      description: 'Former journalist covering elections, now building tools for transparency',
      linkedin: '#',
      twitter: '#'
    }
  ];

  const milestones = [
    {
      year: '2020',
      title: 'Company Founded',
      description: 'Started with a vision to democratize political data in India'
    },
    {
      year: '2021',
      title: 'First Product Launch',
      description: 'Launched basic constituency analytics platform'
    },
    {
      year: '2022',
      title: 'AI Integration',
      description: 'Introduced machine learning for predictive analytics'
    },
    {
      year: '2023',
      title: 'News Integration',
      description: 'Added real-time news monitoring and sentiment analysis'
    },
    {
      year: '2024',
      title: 'API Launch',
      description: 'Opened our platform to developers through comprehensive APIs'
    },
    {
      year: '2025',
      title: 'Major Upgrade',
      description: 'Complete platform redesign with enhanced AI capabilities'
    }
  ];

  const stats = [
    {
      number: '650+',
      label: 'Constituencies Covered',
      icon: <PublicIcon />
    },
    {
      number: '10M+',
      label: 'Data Points Analyzed',
      icon: <AnalyticsIcon />
    },
    {
      number: '1000+',
      label: 'Active Users',
      icon: <GroupsIcon />
    },
    {
      number: '99.9%',
      label: 'Uptime Guarantee',
      icon: <SpeedIcon />
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar position="static" sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)' }}>
        <Toolbar>
          <IconButton 
            edge="start" 
            color="inherit" 
            onClick={() => onNavigate('landing')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src={civiqLogo}
              alt="CivIQ Logo"
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                mr: 1,
                border: '1px solid rgba(255,255,255,0.3)'
              }}
            />
            {/* <AnalyticsIcon sx={{ mr: 1 }} /> */}
            <Typography variant="h6" fontWeight="bold">
              About CivIQ
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box sx={{ py: 8, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              Democratizing
            </Typography>
            <Typography variant="h2" fontWeight="bold" gutterBottom sx={{ 
              background: 'linear-gradient(45deg, #ffd700, #ffeb3b)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Political Intelligence
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: 800, mx: 'auto', mt: 3, opacity: 0.9 }}>
              We believe every citizen, researcher, and journalist should have access to comprehensive, 
              accurate, and real-time political data. CivIQ is making this vision a reality.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Mission Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Our Mission
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
              To transform how India understands and engages with its democracy through 
              accessible, accurate, and actionable political data.
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
              In a country as diverse and complex as India, political data has traditionally been 
              scattered, inaccessible, or difficult to interpret. We're changing that by building 
              the most comprehensive political intelligence platform, powered by AI and designed 
              for everyone from students to seasoned analysts.
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => onNavigate('features')}
              sx={{ px: 4, py: 1.5, fontWeight: 600 }}
            >
              Explore Features
            </Button>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, textAlign: 'center', border: '2px solid #e3f2fd' }}>
              <AnalyticsIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Data-Driven Democracy
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Empowering informed decision-making through comprehensive political analytics
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Stats Section */}
      <Box sx={{ py: 8, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
            Platform Impact
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            Numbers that reflect our commitment to excellence
          </Typography>
          
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ 
                  textAlign: 'center',
                  p: 3,
                  height: '100%',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                  }
                }}>
                  <Box sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 2,
                    background: alpha('#1976d2', 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2
                  }}>
                    {React.cloneElement(stat.icon, { 
                      sx: { color: 'primary.main', fontSize: 32 } 
                    })}
                  </Box>
                  <Typography variant="h3" fontWeight="bold" color="primary.main" gutterBottom>
                    {stat.number}
                  </Typography>
                  <Typography variant="h6" fontWeight="600">
                    {stat.label}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Timeline */}
      {/* <Box sx={{ py: 8, backgroundColor: '#f8fafc' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
            Our Journey
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            Key milestones in building India's premier political data platform
          </Typography>
          
          <Grid container spacing={3}>
            {milestones.map((milestone, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ 
                  p: 3,
                  height: '100%',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)'
                  }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Chip 
                      label={milestone.year}
                      sx={{ 
                        backgroundColor: 'primary.main',
                        color: 'white',
                        fontWeight: 600,
                        mr: 2
                      }}
                    />
                    <TimelineIcon sx={{ color: 'text.secondary' }} />
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {milestone.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {milestone.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box> */}

      {/* Team Section */}
      {/* <Box sx={{ py: 8, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
            Meet Our Team
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            Passionate experts combining technology with political science
          </Typography>
          
          <Grid container spacing={4}>
            {team.map((member, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ 
                  textAlign: 'center',
                  p: 3,
                  height: '100%',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                  }
                }}>
                  <Avatar 
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      mx: 'auto', 
                      mb: 2,
                      background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)',
                      fontSize: '2rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {member.name}
                  </Typography>
                  <Typography variant="subtitle1" color="primary.main" fontWeight="600" gutterBottom>
                    {member.role}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {member.description}
                  </Typography>
                  <Stack direction="row" spacing={1} justifyContent="center">
                    {member.linkedin && (
                      <IconButton size="small" sx={{ color: '#0077b5' }}>
                        <LinkedInIcon />
                      </IconButton>
                    )}
                    {member.twitter && (
                      <IconButton size="small" sx={{ color: '#1da1f2' }}>
                        <TwitterIcon />
                      </IconButton>
                    )}
                    {member.github && (
                      <IconButton size="small" sx={{ color: '#333' }}>
                        <GitHubIcon />
                      </IconButton>
                    )}
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box> */}

      {/* Values Section */}
      <Box sx={{ py: 8, backgroundColor: '#f8fafc' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
            Our Values
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            The principles that guide everything we do
          </Typography>
          
          <Grid container spacing={4}>
            {[
              {
                icon: <VerifiedIcon />,
                title: 'Accuracy',
                description: 'We ensure all data is verified, up-to-date, and sourced from reliable institutions.'
              },
              {
                icon: <PublicIcon />,
                title: 'Transparency',
                description: 'Open about our methodologies, data sources, and any limitations in our analysis.'
              },
              {
                icon: <GroupsIcon />,
                title: 'Accessibility',
                description: 'Making complex political data understandable and accessible to everyone.'
              },
              {
                icon: <TrendingIcon />,
                title: 'Innovation',
                description: 'Constantly improving our platform with the latest technology and user feedback.'
              }
            ].map((value, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ p: 4, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      background: alpha('#1976d2', 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}>
                      {React.cloneElement(value.icon, { 
                        sx: { color: 'primary.main', fontSize: 24 } 
                      })}
                    </Box>
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {value.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {value.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 8, background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)', color: 'white' }}>
        <Container maxWidth="md">
          <Box textAlign="center">
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Join Our Mission
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Be part of the movement to make Indian democracy more transparent and accessible
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button 
                variant="contained" 
                size="large"
                onClick={() => onNavigate('login')}
                sx={{
                  backgroundColor: 'white',
                  color: 'primary.main',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: alpha('#fff', 0.9)
                  }
                }}
              >
                Get Started
              </Button>
              <Button 
                variant="outlined" 
                size="large"
                onClick={() => onNavigate('contact')}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: alpha('#fff', 0.1)
                  }
                }}
              >
                Contact Us
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
      {/* Footer */}
      <Box sx={{ py: 6, backgroundColor: '#263238', color: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" mb={2}>
                <Box 
                  sx={{ 
                    width: 40, 
                    height: 40, 
                    // background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}
                >
                  <Box
              component="img"
              src={civiqLogo}
              alt="CivIQ Logo"
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                mr: 1,
                border: '1px solid rgba(255,255,255,0.3)'
              }}
            />
                </Box>
                <Typography variant="h5" fontWeight="bold">
                  CivIQ
                </Typography>
              </Box>
              <Typography variant="body2" color="rgba(255,255,255,0.7)" sx={{ maxWidth: 400 }}>
                AI-powered political analytics platform for India's elections. Making complex political data accessible and actionable.
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Platform
                  </Typography>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('features')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      Features
                    </Button>
                  </Box>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('pricing')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      Pricing
                    </Button>
                  </Box>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('api')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      API
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Company
                  </Typography>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('about')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      About
                    </Button>
                  </Box>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('contact')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      Contact
                    </Button>
                  </Box>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('privacy')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      Privacy Policy
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.2)' }} />
          
          <Box textAlign="center">
            <Typography variant="body2" color="rgba(255,255,255,0.7)">
              © 2025 CivIQ. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
    
  );
};

export default About;
